import React, { useCallback, useMemo, useRef } from "react";
import { Container, Graphics, Text } from "@pixi/react";
import * as PIXI from "pixi.js";
import { useDispatch, useSelector } from "react-redux";
import { FONT_CONFIG } from "../../../../pixiSettings";
import { useDimensions } from "../../../../Contexts/DimensionsContext";
import {
  setShowFreeSpinIntro,
  setShowAdditionalFreeSpins,
  setShowFreeSpinMaxWin,
  setShowFreeSpinTotalWin,
  setShowFreeSpinZeroWin,
  setShowTransitionDialog,
  setWinAnimationsPlaying,
  setShowHoldAndWinIntro,
  selectShowHoldAndWinIntro,
} from "../../../../State/Slices/animationsSlice";
import {
  setShowHoldAndWinReload,
  selectShowHoldAndWinReload,
  setShowHoldAndWinTotalWin,
  selectShowHoldAndWinTotalWin,
  setShowHoldAndWinRoyal,
  selectShowHoldAndWinRoyal,
} from "../../../../State/Slices/holdAndWinSlice";
import {
  selectShowFreeSpinIntro,
  selectShowAdditionalFreeSpins,
  selectShowFreeSpinMaxWin,
  selectShowFreeSpinTotalWin,
  selectShowFreeSpinZeroWin,
  selectShowTransitionDialog,
  selectWinAnimationsPlaying,
  selectBet,
} from "../../../../State/selectors";
import { useReelRefs } from "../../../../Contexts/ReelRefsContext";
import { PROVIDER_CONFIG } from "../../../../RGS/Providers/PopiPlay/providerConfig";

// The full win-celebration ladder, lowest tier first. Taken from the provider
// so the strings can never drift from the spine skin names — "supermega_win"
// is one word, and a typo there fails silently as a missing skin.
const WIN_TIER_SEQUENCE = [
  PROVIDER_CONFIG.WIN_TYPES.BIG_WIN,
  PROVIDER_CONFIG.WIN_TYPES.MEGA_WIN,
  PROVIDER_CONFIG.WIN_TYPES.SUPER_MEGA_WIN,
];

// What the counter should count up to. The sequence shows each tier's own
// threshold as it arrives, so the final number has to be at least the last
// tier's — otherwise the counter would appear to run backwards.
const WIN_TIER_TARGET_MULTIPLIER =
  PROVIDER_CONFIG.WIN_THRESHOLDS.SUPER_MEGA_WIN_THRESHOLD;

// Panel configuration
//
// Nothing here fixes a width. Buttons are measured from their own labels and
// the panel is measured from the buttons, so the whole thing is only ever as
// wide as it needs to be — and one column means a new button lands underneath
// the last rather than widening the panel.
const PANEL_CONFIG = {
  PADDING: 16,
  BUTTON_HEIGHT: 44,
  BUTTON_GAP: 10,
  BORDER_RADIUS: 10,
  PANEL_ALPHA: 0.85,
  PANEL_COLOR: 0x1e1e2e,
  BUTTON_COLOR: 0x3a3a5c,
  BUTTON_ACTIVE_COLOR: 0x1a6b4a,
  BUTTON_HOVER_COLOR: 0x4a4a6e,
  TEXT_COLOR: 0xffffff,
  ACTIVE_TEXT_COLOR: 0x5effa0,
  TITLE_COLOR: 0xaaaacc,
  FONT_SIZE: 14,
  TITLE_FONT_SIZE: 11,

  // ── Fitting ──
  // The panel is laid out at its natural size and then scaled down as one
  // piece if it will not fit. Everything inside — hit areas included — is in
  // the same local space, so a single scale on the root keeps the buttons
  // clickable exactly where they are drawn.
  MAX_WIDTH_RATIO: 0.45, // never cover more than this share of the screen
  MAX_HEIGHT_RATIO: 0.92,
  MIN_SCALE: 0.35, // below this it is unreadable; let it clip instead
};

/**
 * DevPanel — Development test panel.
 * Gated by FEATURES_CONFIG.DEV_TOOLS.DEV_PANEL.
 * Only for testing popups — no transition/tension triggers.
 */
const DevPanel = () => {
  const dispatch = useDispatch();
  const { dimensions } = useDimensions();
  const freeSpinIntroOpen = useSelector(selectShowFreeSpinIntro);
  const additionalFreeSpinsOpen = useSelector(selectShowAdditionalFreeSpins);
  const freeSpinMaxWinOpen = useSelector(selectShowFreeSpinMaxWin);
  const freeSpinTotalWinOpen = useSelector(selectShowFreeSpinTotalWin);
  const freeSpinZeroWinOpen = useSelector(selectShowFreeSpinZeroWin);
  const transitionOpen = useSelector(selectShowTransitionDialog);
  const holdAndWinIntroOpen = useSelector(selectShowHoldAndWinIntro);
  const holdAndWinReloadOpen = useSelector(selectShowHoldAndWinReload);
  const holdAndWinTotalWinOpen = useSelector(selectShowHoldAndWinTotalWin);
  const holdAndWinRoyalOpen = useSelector(selectShowHoldAndWinRoyal);
  const winAnimationsPlaying = useSelector(selectWinAnimationsPlaying);
  const bet = useSelector(selectBet);
  const {
    winAnimationControllerRef,
    additionalFreeSpinsControllerRef,
    holdAndWinGameViewControllerRef,
  } = useReelRefs();

  const hoverRefs = useRef({});
  const graphicsRefs = useRef({});

  /**
   * Play the whole celebration ladder: big win → mega win → super mega win.
   *
   * There is no round behind this, so the normal route cannot be used —
   * WinAnimationSecondary only starts a sequence when currentRound.win is
   * above zero, and the controller derives its tiers from currentRound.winType.
   * Both are empty here. So the tiers are handed over directly instead, and
   * the win amount is invented purely to give the counter something to count.
   *
   * setWinAnimationsPlaying(true) is still dispatched, because it is what puts
   * the dimming overlay up and makes "tap to continue" clickable. The
   * controller clears it again when the sequence ends, exactly as it does for
   * a real win.
   */
  const playWinTierSequence = useCallback(() => {
    const controller = winAnimationControllerRef?.current;
    if (!controller || winAnimationsPlaying) return;

    const betAmount = bet || 1;
    dispatch(setWinAnimationsPlaying(true));
    controller.startSequence(
      betAmount * WIN_TIER_TARGET_MULTIPLIER,
      betAmount,
      WIN_TIER_SEQUENCE,
    );
  }, [dispatch, bet, winAnimationsPlaying, winAnimationControllerRef]);

  // One entry per thing worth triggering by hand. The Zeus pot-collection
  // buttons that used to live here are gone with that feature.
  //
  // `active` drives the green highlight, so a toggle shows its own state.
  const buttons = useMemo(
    () => [
      {
        // The Hold & Win intro (YOU WON / 3 / CLEOPATRA'S RESPINS), for sizing
        // (2026-09-30). With nothing triggered its close does NOT start the
        // wipe — it just closes (HoldAndWinIntroController).
        id: "hold_and_win_intro",
        label: "H&W Intro",
        active: holdAndWinIntroOpen,
        action: () => dispatch(setShowHoldAndWinIntro(true)),
      },
      {
        // The mid-feature reload popup (YOU HAVE / n / RESPINS LEFT). Closes
        // without a wipe, as live.
        id: "hold_and_win_reload",
        label: "H&W Reload",
        active: holdAndWinReloadOpen,
        action: () => dispatch(setShowHoldAndWinReload(true)),
      },
      {
        // The H&W total win (hold_and_win_outro). With no round it counts to
        // 125x bet and closes without the wipe (FreeSpinTotalWinDialog / its
        // controller).
        id: "hold_and_win_total_win",
        // label: "H&W Total Win",
        label: "H&W Total Win (outro)",
        active: holdAndWinTotalWinOpen,
        action: () => dispatch(setShowHoldAndWinTotalWin(true)),
      },
      {
        // The H&W Royal award (zero_win placeholder): 5,000x bet, closes by
        // itself after ~3 s or on a tap, never a wipe.
        id: "hold_and_win_royal",
        label: "H&W Royal (zero_win)",
        active: holdAndWinRoyalOpen,
        action: () => dispatch(setShowHoldAndWinRoyal(true)),
      },
      {
        // The bowls' fire flare a row unlock plays (POT_FIRE_PULSE,
        // 2026-10-15). Needs the H&W room on screen (in H&W, or
        // PREVIEW_HOLD_AND_WIN_VIEW).
        id: "hold_and_win_fire_pulse",
        label: "H&W Fire Pulse",
        active: false,
        action: () => holdAndWinGameViewControllerRef?.current?.playPotFirePulse?.(),
      },
      {
        // Closes itself on click or spacebar, so this only ever opens it.
        id: "free_spin_intro",
        // label: "Free Spin Intro",
        label: "FS Intro",
        active: freeSpinIntroOpen,
        action: () => dispatch(setShowFreeSpinIntro(true)),
      },
      {
        // ── Raising the flag is NOT opening the popup ──
        //
        // This used to dispatch the flag and stop there, which is the one state
        // the popup must never be left in: the dialog deliberately does not
        // start itself from `show` (the level-up sequence starts it, with the
        // hooks it needs), so nothing ever opened and the flag stayed up. That
        // flag is a hard block on the free-spin loop AND, since the popup was
        // made modal, a screen-sized absorber — so this button locked the game.
        //
        // It now drives the controller the way the win-tier button does, with
        // Mina's exact case from demo 2: +7 spins, no box closed, "the one
        // without a stage". If the controller refuses for any reason it clears
        // the flag itself, so this cannot leave the screen stuck either way.
        id: "additional_free_spins",
        // label: "Additional Free Spins (+7)",
        label: "FS Retrigger (+7)",
        active: additionalFreeSpinsOpen,
        action: () => {
          const controller = additionalFreeSpinsControllerRef?.current;
          if (!controller) return;
          controller.setNoBoxClosed(true);
          controller.setLevel(1);
          controller.setSpinCount(7);
          dispatch(setShowAdditionalFreeSpins(true));
          controller.startSequence({});
        },
      },
      {
        id: "free_spin_max_win",
        // label: "Free Spin Max Win",
        label: "FS Max Win (max_win)",
        active: freeSpinMaxWinOpen,
        action: () => dispatch(setShowFreeSpinMaxWin(true)),
      },
      {
        id: "free_spin_total_win",
        // label: "Free Spin Total Win",
        label: "FS Total Win (outro)",
        active: freeSpinTotalWinOpen,
        action: () => dispatch(setShowFreeSpinTotalWin(true)),
      },
      {
        id: "free_spin_zero_win",
        // label: "Free Spin Zero Win",
        label: "FS Better Luck (zero_win)",
        active: freeSpinZeroWinOpen,
        action: () => dispatch(setShowFreeSpinZeroWin(true)),
      },
      {
        // ── All four tap words on screen at once ──
        //
        // The Zeus panel had this and it is the only honest way to check the
        // prompt: they are supposed to sit on the SAME screen line at the SAME
        // size, and four popups opened one after another can only be compared
        // from memory. Opened together, a misalignment is a staircase.
        //
        // These four, and only these four: every one of them draws its word
        // through attachTapToContinue, so they are exactly the set the anchor
        // governs. Additional Free Spins is left out on purpose — it has no tap
        // word at all, and its dim would sit over the ones being compared.
        //
        // They stack, so the dims darken and the topmost one takes the taps.
        // Tap through them to close them one at a time.
        id: "all_tap_popups",
        label: "All Tap Popups",
        active:
          freeSpinIntroOpen &&
          freeSpinMaxWinOpen &&
          freeSpinTotalWinOpen &&
          freeSpinZeroWinOpen,
        action: () => {
          dispatch(setShowFreeSpinIntro(true));
          dispatch(setShowFreeSpinMaxWin(true));
          dispatch(setShowFreeSpinTotalWin(true));
          dispatch(setShowFreeSpinZeroWin(true));
        },
      },
      {
        // Plays once and closes itself — nothing to toggle off.
        id: "transition",
        label: "Transition",
        active: transitionOpen,
        action: () => dispatch(setShowTransitionDialog(true)),
      },
      {
        // The whole ladder in one press: big → mega → super mega, counter and
        // sounds included. Runs itself to the end and closes.
        id: "win_tier_sequence",
        label: "Super Mega Win",
        active: winAnimationsPlaying,
        action: playWinTierSequence,
      },
    ],
    [
      dispatch,
      holdAndWinIntroOpen,
      holdAndWinReloadOpen,
      holdAndWinTotalWinOpen,
      holdAndWinRoyalOpen,
      freeSpinIntroOpen,
      additionalFreeSpinsOpen,
      additionalFreeSpinsControllerRef,
      holdAndWinGameViewControllerRef,
      freeSpinMaxWinOpen,
      freeSpinTotalWinOpen,
      freeSpinZeroWinOpen,
      transitionOpen,
      winAnimationsPlaying,
      playWinTierSequence,
    ],
  );

  const { PADDING, BUTTON_HEIGHT, BUTTON_GAP, BORDER_RADIUS } = PANEL_CONFIG;

  const labelStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: PANEL_CONFIG.FONT_SIZE,
        fontWeight: "700",
      }),
    [],
  );

  // Recomputed only when the LABELS change, not when a toggle flips — the
  // `buttons` array is rebuilt on every state change and would otherwise
  // re-measure text on each click.
  const labelsKey = buttons.map((b) => b.label).join("|");

  /**
   * Button width, measured rather than declared.
   *
   * The side padding is set to match the padding the label already has above
   * and below, so the button hugs its word instead of sitting in a fixed 160.
   * Vertical padding is whatever BUTTON_HEIGHT leaves around the text, so the
   * height stays as authored and only the width follows.
   *
   * Every button gets the WIDEST label's width, so the column has one edge.
   * The active state prefixes a bullet, so that variant is what gets measured
   * — otherwise a button would resize the moment it was toggled.
   */
  const buttonWidth = useMemo(() => {
    const measured = buttons.map((b) =>
      PIXI.TextMetrics.measureText(`● ${b.label}`, labelStyle),
    );
    if (!measured.length) return 0;
    const widest = Math.max(...measured.map((m) => m.width));
    const padY = (BUTTON_HEIGHT - measured[0].height) / 2;
    return Math.ceil(widest + padY * 2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [labelsKey, labelStyle, BUTTON_HEIGHT]);

  const panelWidth = PADDING * 2 + buttonWidth;
  const titleHeight = 20;
  const rows = buttons.length;
  const panelHeight =
    PADDING * 2 + titleHeight + rows * BUTTON_HEIGHT + (rows - 1) * BUTTON_GAP;

  const panelX = 10;
  const panelY = 10;

  /**
   * Shrink-to-fit, never grow.
   *
   * The panel's natural size comes from its longest label and its button
   * count, so it grows as buttons are added and had no relationship to the
   * viewport at all — on a short screen it simply ran off the bottom. This
   * scales the whole thing down when it would not fit, and leaves it alone
   * when it would.
   */
  const fitScale = useMemo(() => {
    const footprintW = panelX + panelWidth;
    const footprintH = panelY + panelHeight;
    const w = dimensions?.width || 0;
    const h = dimensions?.height || 0;
    if (!w || !h || !footprintW || !footprintH) return 1;

    const scale = Math.min(
      1,
      (w * PANEL_CONFIG.MAX_WIDTH_RATIO) / footprintW,
      (h * PANEL_CONFIG.MAX_HEIGHT_RATIO) / footprintH,
    );
    return Math.max(PANEL_CONFIG.MIN_SCALE, scale);
  }, [dimensions?.width, dimensions?.height, panelWidth, panelHeight]);

  const titleStyle = useMemo(
    () =>
      new PIXI.TextStyle({
        fontFamily: FONT_CONFIG.PRIMARY,
        fontSize: PANEL_CONFIG.TITLE_FONT_SIZE,
        fontWeight: "600",
        fill: PANEL_CONFIG.TITLE_COLOR,
        letterSpacing: 1,
      }),
    []
  );

  const drawPanel = useCallback(
    (g) => {
      g.clear();
      g.beginFill(PANEL_CONFIG.PANEL_COLOR, PANEL_CONFIG.PANEL_ALPHA);
      g.lineStyle(1, 0x333355, 0.5);
      g.drawRoundedRect(panelX, panelY, panelWidth, panelHeight, BORDER_RADIUS);
      g.endFill();
    },
    [panelX, panelY, panelWidth, panelHeight, BORDER_RADIUS]
  );

  const drawButton = useCallback(
    (g, btnIndex) => {
      const btn = buttons[btnIndex];
      const bx = panelX + PADDING;
      const by =
        panelY + PADDING + titleHeight + btnIndex * (BUTTON_HEIGHT + BUTTON_GAP);
      const isHovered = hoverRefs.current[btn.id] || false;
      const color = btn.active
        ? PANEL_CONFIG.BUTTON_ACTIVE_COLOR
        : isHovered
          ? PANEL_CONFIG.BUTTON_HOVER_COLOR
          : PANEL_CONFIG.BUTTON_COLOR;

      g.clear();
      g.beginFill(color, 0.9);
      g.lineStyle(1, btn.active ? PANEL_CONFIG.ACTIVE_TEXT_COLOR : 0x444466, 0.6);
      g.drawRoundedRect(bx, by, buttonWidth, BUTTON_HEIGHT, 4);
      g.endFill();
    },
    [buttons, panelX, panelY, PADDING, titleHeight, buttonWidth, BUTTON_HEIGHT, BUTTON_GAP]
  );

  return (
    <Container
      name="dev_panel"
      zIndex={9999}
      sortableChildren
      scale={fitScale}
    >
      <Graphics draw={drawPanel} />
      <Text
        text="DEV PANEL"
        style={titleStyle}
        anchor={[0.5, 0]}
        x={panelX + panelWidth / 2}
        y={panelY + PADDING}
        resolution={2}
      />
      {buttons.map((btn, i) => {
        const bx = panelX + PADDING;
        const by =
          panelY + PADDING + titleHeight + i * (BUTTON_HEIGHT + BUTTON_GAP);

        const btnTextStyle = new PIXI.TextStyle({
          fontFamily: FONT_CONFIG.PRIMARY,
          fontSize: PANEL_CONFIG.FONT_SIZE,
          fontWeight: "700",
          fill: btn.active ? PANEL_CONFIG.ACTIVE_TEXT_COLOR : PANEL_CONFIG.TEXT_COLOR,
        });

        return (
          <Container key={btn.id} name={`dev_btn_${btn.id}`}>
            <Graphics
              ref={(ref) => { if (ref) graphicsRefs.current[btn.id] = ref; }}
              draw={(g) => drawButton(g, i)}
              eventMode="static"
              cursor="pointer"
              hitArea={new PIXI.Rectangle(bx, by, buttonWidth, BUTTON_HEIGHT)}
              onpointertap={(e) => { e.stopPropagation(); btn.action(); }}
              onpointerover={() => {
                hoverRefs.current[btn.id] = true;
                const gfx = graphicsRefs.current[btn.id];
                if (gfx) drawButton(gfx, i);
              }}
              onpointerout={() => {
                hoverRefs.current[btn.id] = false;
                const gfx = graphicsRefs.current[btn.id];
                if (gfx) drawButton(gfx, i);
              }}
            />
            <Text
              text={btn.active ? `● ${btn.label}` : btn.label}
              style={btnTextStyle}
              anchor={0.5}
              x={bx + buttonWidth / 2}
              y={by + BUTTON_HEIGHT / 2}
              resolution={2}
              eventMode="none"
            />
          </Container>
        );
      })}
    </Container>
  );
};

export default DevPanel;
